import React from 'react';
import { Box } from '@mui/material';
import { gray5, gray30, gray80, white } from '../../../../constants/colors';

/**
 * 検証ログの表構造（DI の <table> HTML）を安全に表としてレンダリングする（#3109）。
 *
 * table_text には DI が出力した HTML の <table> ブロックが入っている。
 * dangerouslySetInnerHTML で生 HTML を流し込むと XSS リスクがあるため、
 * DOMParser でパースし、許可したタグ（table/thead/tbody/tr/td/th）と
 * 属性（rowspan/colspan）だけを React 要素に変換して描画する。
 * スクリプトや未知のタグは無視されるため、帳票内容に何が入っていても安全。
 *
 * 表以外のテキスト（table_text 内に混在した通常テキスト）はそのまま段落として出す。
 */

type VerificationTableViewProps = {
  /** DI の <table> を含む表構造テキスト */
  html: string;
};

// セル（td/th）を React 要素に変換する
const renderCell = (cell: Element, key: number): JSX.Element => {
  const isHeader = cell.tagName.toLowerCase() === 'th';
  const rowSpan = Number(cell.getAttribute('rowspan')) || undefined;
  const colSpan = Number(cell.getAttribute('colspan')) || undefined;
  const text = cell.textContent ?? '';
  return (
    <Box
      component={isHeader ? 'th' : 'td'}
      key={key}
      rowSpan={rowSpan}
      colSpan={colSpan}
      sx={{
        border: `1px solid ${gray30}`,
        padding: '4px 6px',
        fontSize: '11px',
        lineHeight: 1.5,
        textAlign: 'left',
        verticalAlign: 'top',
        color: gray80,
        backgroundColor: isHeader ? gray5 : white,
        fontWeight: isHeader ? 700 : 400,
        // 各セルは最低幅を確保して縦に潰れないようにしつつ、
        // 長文セルは最大幅内で折り返して横に伸びすぎないようにする。
        minWidth: '48px',
        maxWidth: '240px',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  );
};

// 1つの <table> 要素を React のテーブルに変換する
const renderTable = (table: Element, key: number): JSX.Element => {
  const rows = Array.from(table.querySelectorAll('tr'));
  return (
    // 列が多い／横に広い表はパネル幅に押し込めず、横スクロールで全体を確認できるようにする。
    // ラッパを overflow-x: auto にし、table 側は width:auto + tableLayout:auto で
    // 内容に応じた幅に伸ばす（各セルは最低幅を確保して縦に潰れないようにする）。
    <Box key={key} sx={{ overflowX: 'auto', marginBottom: '12px' }}>
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          width: 'auto',
          tableLayout: 'auto',
        }}
      >
        <Box component="tbody">
          {rows.map((row, rowIndex) => (
            <Box component="tr" key={rowIndex}>
              {Array.from(row.children)
                .filter(c => {
                  const tag = c.tagName.toLowerCase();
                  return tag === 'td' || tag === 'th';
                })
                .map((cell, cellIndex) => renderCell(cell, cellIndex))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const VerificationTableView = ({ html }: VerificationTableViewProps): JSX.Element => {
  // DOMParser で安全にパースする（このHTMLは描画せず要素の読み取りのみに使う）
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nodes = Array.from(doc.body.childNodes);

  const elements: JSX.Element[] = [];
  nodes.forEach((node, index) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (el.tagName.toLowerCase() === 'table') {
        elements.push(renderTable(el, index));
        return;
      }
    }
    // 表以外のテキスト（見出しや区切りなど）はプレーンテキストの段落として出す
    const text = node.textContent?.trim();
    if (text) {
      elements.push(
        <Box
          key={index}
          sx={{
            fontSize: '11px',
            color: gray80,
            marginBottom: '8px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {text}
        </Box>,
      );
    }
  });

  return <Box>{elements}</Box>;
};

export default VerificationTableView;
